import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { PosterCard } from "../components/PosterCard";
import { Plus, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user) {
      api.get("/episodes").then(({ data }) => {
        setEpisodes(data);
      }).catch(() => {}).finally(() => setLoading(false));
    }
  }, [user, authLoading, navigate]);

  const firstName = user?.name?.split(" ")[0] || "You";

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="dashboard-page">
      <Navbar />

      <div className="pt-28 pb-32 px-6 sm:px-12 max-w-7xl mx-auto">
        {/* Hero greeting */}
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16 cinema-fade-in">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF2A2A] mb-4">
              Now playing · You
            </div>
            <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter leading-none">
              {firstName.toUpperCase()}<span className="text-[#FF2A2A]">.</span>
            </h1>
            <p className="font-emotional text-xl text-white/60 mt-4 max-w-lg">
              Your collection of moments, edited like cinema.
            </p>
          </div>
          <Link
            to="/create"
            data-testid="create-episode-button"
            className="group inline-flex items-center gap-3 bg-[#FF2A2A] hover:bg-[#FF5555] text-white font-mono uppercase tracking-widest text-sm px-8 py-4 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New episode
          </Link>
        </div>

        {/* Episodes */}
        {renderEpisodesSection({ loading, episodes })}
      </div>
    </div>
  );
}

function renderEpisodesSection({ loading, episodes }) {
  if (loading) {
    return <div className="font-mono text-xs uppercase tracking-widest text-white/40">Loading reel...</div>;
  }
  if (episodes.length === 0) {
    return <EmptyState />;
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-display font-bold text-2xl tracking-tight">
          Your Episodes
          <span className="font-mono text-xs uppercase tracking-widest text-white/30 ml-3">
            {episodes.length} TITLE{episodes.length === 1 ? "" : "S"}
          </span>
        </h2>
      </div>
      <div data-testid="episodes-row" className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-8 scrollbar-hide -mx-6 sm:-mx-12 px-6 sm:px-12">
        {episodes.map((ep, i) => (
          <PosterCard key={ep.id} episode={ep} index={i} />
        ))}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div data-testid="empty-state" className="border border-dashed border-white/15 p-12 sm:p-20 text-center max-w-3xl mx-auto">
      <Sparkles className="w-10 h-10 text-[#FF2A2A] mx-auto mb-6" />
      <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">
        Your reel is empty.
      </h3>
      <p className="font-emotional text-lg text-white/60 mt-4 max-w-md mx-auto">
        Drop your first batch of photos and let AI cut it into a cinematic trailer.
      </p>
      <Link
        to="/create"
        data-testid="empty-state-cta"
        className="inline-flex mt-8 items-center gap-2 bg-[#FF2A2A] hover:bg-[#FF5555] text-white font-mono uppercase tracking-widest text-xs px-8 py-4 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Make episode 01
      </Link>
    </div>
  );
}
